import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Database, RefreshCw, Image, QrCode } from 'lucide-react';
import { Button } from '../ui/button';
import { api } from '../../lib/store';

function fmtBytes(b) {
  if (!b && b !== 0) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export default function StorageStatsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await api.getStorageStats();
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load storage stats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <Card className="rounded-2xl border-heda-line bg-white shadow-none" data-testid="storage-stats-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-heda-ink flex items-center gap-2">
            <Database className="w-4 h-4 text-heda-gold" /> Storage (MySQL)
          </h3>
          <Button
            variant="ghost" size="sm"
            onClick={load}
            disabled={loading}
            className="rounded-full hover:bg-heda-cream h-7 px-2 text-xs"
            data-testid="storage-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {error && <p className="text-xs text-red-700">{error}</p>}

        {data && (
          <>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="bg-heda-cream rounded-lg p-2 text-center">
                <p className="text-[11px] text-heda-mute uppercase tracking-wide">Total</p>
                <p className="text-lg font-bold text-heda-ink tabular-nums">
                  {fmtBytes(data.total.bytes)}
                </p>
                <p className="text-[11px] text-heda-sub">{data.total.count} file{data.total.count === 1 ? '' : 's'}</p>
              </div>
              <div className="bg-heda-cream rounded-lg p-2 text-center">
                <p className="text-[11px] text-heda-mute uppercase tracking-wide flex items-center justify-center gap-1">
                  <Image className="w-3 h-3" /> Products
                </p>
                <p className="text-lg font-bold text-heda-ink tabular-nums">
                  {fmtBytes(data.product_images.bytes)}
                </p>
                <p className="text-[11px] text-heda-sub">{data.product_images.count}</p>
              </div>
              <div className="bg-heda-cream rounded-lg p-2 text-center">
                <p className="text-[11px] text-heda-mute uppercase tracking-wide flex items-center justify-center gap-1">
                  <QrCode className="w-3 h-3" /> QR Lists
                </p>
                <p className="text-lg font-bold text-heda-ink tabular-nums">
                  {fmtBytes(data.qr_price_lists.bytes)}
                </p>
                <p className="text-[11px] text-heda-sub">{data.qr_price_lists.count}</p>
              </div>
            </div>

            {(data.largest_product_images.length > 0 || data.largest_qr_images.length > 0) && (
              <details className="text-xs text-heda-sub">
                <summary className="cursor-pointer hover:text-heda-ink py-1">
                  Largest files (top 5 each)
                </summary>
                <div className="mt-2 space-y-1 pl-2">
                  {data.largest_product_images.slice(0, 5).map((f, i) => (
                    <div key={i} className="flex justify-between gap-2">
                      <span className="truncate flex-1" title={f.filename}>
                        <Image className="w-3 h-3 inline mr-1" />
                        {f.filename}
                      </span>
                      <span className="tabular-nums text-heda-ink shrink-0">{fmtBytes(f.size_bytes)}</span>
                    </div>
                  ))}
                  {data.largest_qr_images.slice(0, 5).map((f, i) => (
                    <div key={`qr-${i}`} className="flex justify-between gap-2">
                      <span className="truncate flex-1" title={f.label}>
                        <QrCode className="w-3 h-3 inline mr-1" />
                        {f.label || f.id}
                      </span>
                      <span className="tabular-nums text-heda-ink shrink-0">{fmtBytes(f.size_bytes)}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </>
        )}

        {loading && !data && <p className="text-xs text-heda-mute">Loading…</p>}
      </CardContent>
    </Card>
  );
}
